import { causes, institutions, initiatives, IMG, type Cause } from "./sample";

// ============================================================
// Aggregator network — partner organisations, campaigns,
// standard programs, payouts, applications, audit log.
// Frontend sample data; drop-in ready for a real backend.
// ============================================================

export type PartnerStatus = "Live" | "Under review" | "Docs pending" | "Suspended";

export type PartnerOrg = {
  id: string;
  name: string;
  type: string;
  domain: string;
  city: string;
  state: string;
  verified: boolean;
  has80G: boolean;
  status: PartnerStatus;
  joined: string;
  cover: string;
  about: string;
  raised: number;
  donors: number;
  liveCampaigns: number;
  payoutPending: number;
  complianceScore: number;
  institutionId?: string;
};

const animalPartners: PartnerOrg[] = institutions.map((i, idx) => ({
  id: i.id,
  name: i.name,
  type: i.type,
  domain: "animal-seva",
  city: i.city,
  state: i.state,
  verified: i.verified,
  has80G: i.has80G,
  status: i.verified ? "Live" : "Under review",
  joined: `${2019 + (idx % 6)}`,
  cover: i.cover,
  about: i.about,
  raised: i.totalDonations,
  donors: 120 + idx * 47,
  liveCampaigns: i.activeCauses,
  payoutPending: 18000 + idx * 7400,
  complianceScore: i.verified ? 88 + (idx % 10) : 54 + (idx % 12),
  institutionId: i.id,
}));

const otherPartners: PartnerOrg[] = [
  {
    id: "vidya-setu-foundation",
    name: "Vidya Setu Foundation",
    type: "Education NGO",
    domain: "shiksha-daan",
    city: "Ahmedabad",
    state: "Gujarat",
    verified: true,
    has80G: true,
    status: "Live",
    joined: "2021",
    cover: IMG.eventTree,
    about:
      "Runs 14 after-school learning centres for children of migrant construction workers across Ahmedabad and Gandhinagar.",
    raised: 3840000,
    donors: 612,
    liveCampaigns: 3,
    payoutPending: 96000,
    complianceScore: 94,
  },
  {
    id: "arogya-mitra-trust",
    name: "Arogya Mitra Trust",
    type: "Healthcare Trust",
    domain: "arogya-seva",
    city: "Surat",
    state: "Gujarat",
    verified: true,
    has80G: true,
    status: "Live",
    joined: "2022",
    cover: IMG.eventMedical,
    about:
      "Mobile diagnostic vans and free eye-care camps for villages within a 90 km radius of Surat.",
    raised: 2160000,
    donors: 388,
    liveCampaigns: 2,
    payoutPending: 142000,
    complianceScore: 91,
  },
  {
    id: "annapurna-seva-mandal",
    name: "Annapurna Seva Mandal",
    type: "Community Kitchen",
    domain: "anna-daan",
    city: "Vadodara",
    state: "Gujarat",
    verified: true,
    has80G: true,
    status: "Live",
    joined: "2020",
    cover: IMG.eventKitchen,
    about:
      "Serves 4,200 hot meals every day to hospital attendants, daily-wage workers and the elderly.",
    raised: 5120000,
    donors: 1204,
    liveCampaigns: 4,
    payoutPending: 210000,
    complianceScore: 96,
  },
  {
    id: "nari-udaan-sangh",
    name: "Nari Udaan Sangh",
    type: "Women's Collective",
    domain: "nari-shakti",
    city: "Rajkot",
    state: "Gujarat",
    verified: false,
    has80G: true,
    status: "Docs pending",
    joined: "2026",
    cover: IMG.eventRation,
    about:
      "Tailoring, dairy and handicraft self-help groups for 900+ women across Saurashtra villages.",
    raised: 486000,
    donors: 94,
    liveCampaigns: 1,
    payoutPending: 0,
    complianceScore: 62,
  },
  {
    id: "prakriti-rakshak-samiti",
    name: "Prakriti Rakshak Samiti",
    type: "Environment NGO",
    domain: "prakriti-seva",
    city: "Bhavnagar",
    state: "Gujarat",
    verified: true,
    has80G: false,
    status: "Under review",
    joined: "2025",
    cover: IMG.eventTree,
    about:
      "Native-species plantation, lake restoration and school eco-clubs across coastal Saurashtra.",
    raised: 742000,
    donors: 168,
    liveCampaigns: 2,
    payoutPending: 41000,
    complianceScore: 74,
  },
];

export const partners: PartnerOrg[] = [...animalPartners, ...otherPartners];

export const partnerTypes = Array.from(new Set(partners.map((p) => p.type)));

export function getPartner(id: string) {
  return partners.find((p) => p.id === id);
}

export function domainName(slug: string) {
  return initiatives.find((i) => i.slug === slug)?.name ?? slug;
}

// ---------------- Campaigns ----------------

export type CampaignStatus = "Live" | "Under review" | "Funded" | "Closed";

export type Campaign = {
  id: string;
  title: string;
  orgId: string;
  domain: string;
  image: string;
  summary: string;
  story: string;
  goal: number;
  raised: number;
  donors: number;
  startDate: string;
  endDate: string;
  status: CampaignStatus;
  urgency: "Normal" | "Urgent" | "Critical";
  causeId?: string;
  budget: { head: string; amount: number }[];
};

function fromCause(c: Cause): Campaign {
  return {
    id: c.id,
    title: c.title,
    orgId: c.institutionId,
    domain: "animal-seva",
    image: c.image,
    summary: c.shortDesc,
    story: c.fullDesc,
    goal: c.required,
    raised: c.raised,
    donors: c.donors,
    startDate: "2026-05-01",
    endDate: c.endDate,
    status: c.raised >= c.required ? "Funded" : "Live",
    urgency: c.urgency,
    causeId: c.id,
    budget: [
      { head: "Direct programme cost", amount: Math.round(c.required * 0.78) },
      { head: "Logistics & transport", amount: Math.round(c.required * 0.1) },
      { head: "Documentation & audit", amount: Math.round(c.required * 0.06) },
      { head: "Platform & payment fee", amount: Math.round(c.required * 0.06) },
    ],
  };
}

const crossDomainCampaigns: Campaign[] = [
  {
    id: "school-kits-2000-children",
    title: "School kits for 2,000 migrant workers' children",
    orgId: "vidya-setu-foundation",
    domain: "shiksha-daan",
    image: IMG.eventTree,
    summary: "Rs 640 gives one child a full year of books, uniform, bag and stationery.",
    story:
      "Before the June session begins, Vidya Setu equips every enrolled child at its 14 learning centres with a complete school kit so no admission is lost for want of a uniform or notebook.",
    goal: 1280000,
    raised: 736000,
    donors: 412,
    startDate: "2026-04-10",
    endDate: "2026-08-15",
    status: "Live",
    urgency: "Urgent",
    budget: [
      { head: "Books & stationery", amount: 620000 },
      { head: "Uniforms & bags", amount: 480000 },
      { head: "Distribution logistics", amount: 110000 },
      { head: "Platform & payment fee", amount: 70000 },
    ],
  },
  {
    id: "village-eye-camp-series",
    title: "12 free eye camps across rural South Gujarat",
    orgId: "arogya-mitra-trust",
    domain: "arogya-seva",
    image: IMG.eventMedical,
    summary: "Rs 1,100 funds one cataract screening, spectacles and follow-up.",
    story:
      "A year-long series of 12 camps covering 60 villages: screening, cataract surgery referrals, free spectacles and a three-month follow-up visit for every patient treated.",
    goal: 950000,
    raised: 402000,
    donors: 236,
    startDate: "2026-03-01",
    endDate: "2026-12-31",
    status: "Live",
    urgency: "Normal",
    budget: [
      { head: "Camp operations & staff", amount: 430000 },
      { head: "Spectacles & medicines", amount: 320000 },
      { head: "Surgery referrals", amount: 150000 },
      { head: "Platform & payment fee", amount: 50000 },
    ],
  },
  {
    id: "monsoon-ration-1500-families",
    title: "Monsoon ration kits for 1,500 families",
    orgId: "annapurna-seva-mandal",
    domain: "anna-daan",
    image: IMG.eventRation,
    summary: "Rs 1,450 feeds a family of five for a full month of the monsoon.",
    story:
      "Daily-wage work stops when the rains arrive. This drive delivers a 12-item dry ration kit to 1,500 vetted families across Vadodara's informal settlements.",
    goal: 2175000,
    raised: 1892000,
    donors: 908,
    startDate: "2026-05-15",
    endDate: "2026-07-31",
    status: "Live",
    urgency: "Critical",
    budget: [
      { head: "Ration kits", amount: 1830000 },
      { head: "Packing & delivery", amount: 210000 },
      { head: "Verification & audit", amount: 65000 },
      { head: "Platform & payment fee", amount: 70000 },
    ],
  },
  {
    id: "tailoring-units-saurashtra",
    title: "40 tailoring units for women's self-help groups",
    orgId: "nari-udaan-sangh",
    domain: "nari-shakti",
    image: IMG.eventKitchen,
    summary: "Rs 11,000 sets up one woman with a machine, training and market linkage.",
    story:
      "Each unit includes a sewing machine, a 45-day skill course, raw material for the first two orders and onboarding to a bulk-order buyer network.",
    goal: 440000,
    raised: 128000,
    donors: 61,
    startDate: "2026-06-01",
    endDate: "2026-10-30",
    status: "Under review",
    urgency: "Normal",
    budget: [
      { head: "Machines & equipment", amount: 280000 },
      { head: "Training programme", amount: 96000 },
      { head: "Raw material seed stock", amount: 44000 },
      { head: "Platform & payment fee", amount: 20000 },
    ],
  },
  {
    id: "coastal-plantation-25000-trees",
    title: "25,000 native trees along the Bhavnagar coast",
    orgId: "prakriti-rakshak-samiti",
    domain: "prakriti-seva",
    image: IMG.eventTree,
    summary: "Rs 120 plants and protects one native sapling for three years.",
    story:
      "Mangrove and native-species plantation with three-year survival monitoring, executed with 22 coastal village panchayats and school eco-clubs.",
    goal: 3000000,
    raised: 612000,
    donors: 174,
    startDate: "2026-06-20",
    endDate: "2027-03-31",
    status: "Live",
    urgency: "Normal",
    budget: [
      { head: "Saplings & tree guards", amount: 1950000 },
      { head: "Plantation labour", amount: 620000 },
      { head: "3-year monitoring", amount: 310000 },
      { head: "Platform & payment fee", amount: 120000 },
    ],
  },
];

export const campaigns: Campaign[] = [...causes.map(fromCause), ...crossDomainCampaigns];

export function getCampaign(id: string) {
  return campaigns.find((c) => c.id === id);
}

export function campaignPct(c: Campaign) {
  return Math.min(100, Math.round((c.raised / c.goal) * 100));
}

export function daysLeft(endDate: string) {
  const ms = new Date(endDate).getTime() - new Date("2026-07-27").getTime();
  return Math.max(0, Math.ceil(ms / 86400000));
}

// ---------------- Standard (always-on) programs ----------------

export type Program = {
  id: string;
  orgId: string;
  domain: string;
  title: string;
  impact: string;
  monthly: number;
  subscribers: number;
};

export const programs: Program[] = [
  { id: "pg-fodder", orgId: partners[0].id, domain: "animal-seva", title: "Daily fodder seva", impact: "Feeds 5 cattle every day for a month", monthly: 2100, subscribers: 486 },
  { id: "pg-medical", orgId: partners[1]?.id ?? partners[0].id, domain: "animal-seva", title: "Animal medical fund", impact: "Covers medicines & dressings for 20 rescues", monthly: 1100, subscribers: 312 },
  { id: "pg-birds", orgId: partners[2]?.id ?? partners[0].id, domain: "animal-seva", title: "Bird rescue standby", impact: "Keeps a rescue team on call for injured birds", monthly: 501, subscribers: 903 },
  { id: "pg-child", orgId: "vidya-setu-foundation", domain: "shiksha-daan", title: "Sponsor a child's learning", impact: "One child's tuition, kit and meals for a month", monthly: 1500, subscribers: 274 },
  { id: "pg-meals", orgId: "annapurna-seva-mandal", domain: "anna-daan", title: "Daily meals seva", impact: "Serves 30 hot meals every month", monthly: 900, subscribers: 1148 },
  { id: "pg-health", orgId: "arogya-mitra-trust", domain: "arogya-seva", title: "Village health fund", impact: "Screens 8 patients at a rural camp", monthly: 1200, subscribers: 189 },
];

// ---------------- Payouts, applications, audit ----------------

export type Payout = {
  id: string;
  orgId: string;
  period: string;
  gross: number;
  fee: number;
  net: number;
  status: "Paid" | "Processing" | "On hold" | "Scheduled";
  utr?: string;
  date: string;
};

export const payouts: Payout[] = [
  { id: "PO-2604", orgId: partners[0].id, period: "16-30 Jun 2026", gross: 384200, fee: 11526, net: 372674, status: "Paid", utr: "HDFC0026041188", date: "2026-07-02" },
  { id: "PO-2605", orgId: "annapurna-seva-mandal", period: "16-30 Jun 2026", gross: 512400, fee: 15372, net: 497028, status: "Paid", utr: "ICIC0026051042", date: "2026-07-02" },
  { id: "PO-2606", orgId: "vidya-setu-foundation", period: "01-15 Jul 2026", gross: 218600, fee: 6558, net: 212042, status: "Processing", date: "2026-07-18" },
  { id: "PO-2607", orgId: "arogya-mitra-trust", period: "01-15 Jul 2026", gross: 142000, fee: 4260, net: 137740, status: "On hold", date: "2026-07-18" },
  { id: "PO-2608", orgId: partners[1]?.id ?? partners[0].id, period: "16-31 Jul 2026", gross: 96400, fee: 2892, net: 93508, status: "Scheduled", date: "2026-08-02" },
  { id: "PO-2609", orgId: "prakriti-rakshak-samiti", period: "16-31 Jul 2026", gross: 41000, fee: 1230, net: 39770, status: "Scheduled", date: "2026-08-02" },
];

export type PartnerApplication = {
  id: string;
  org: string;
  type: string;
  domain: string;
  city: string;
  submitted: string;
  stage: "Applied" | "Under review" | "Docs verified" | "Approved" | "Rejected";
  docs: number;
  docsTotal: number;
};

export const applications: PartnerApplication[] = [
  { id: "AP-1041", org: "Shree Palanpur Panjrapole", type: "Panjrapol", domain: "animal-seva", city: "Palanpur", submitted: "2026-07-21", stage: "Under review", docs: 5, docsTotal: 6 },
  { id: "AP-1040", org: "Sneh Balgruh", type: "Child Care Home", domain: "shiksha-daan", city: "Nadiad", submitted: "2026-07-19", stage: "Applied", docs: 3, docsTotal: 6 },
  { id: "AP-1039", org: "Jeevan Aadhar Blood Bank", type: "Healthcare Trust", domain: "arogya-seva", city: "Mehsana", submitted: "2026-07-16", stage: "Docs verified", docs: 6, docsTotal: 6 },
  { id: "AP-1038", org: "Van Mitra Samiti", type: "Environment NGO", domain: "prakriti-seva", city: "Valsad", submitted: "2026-07-12", stage: "Approved", docs: 6, docsTotal: 6 },
  { id: "AP-1037", org: "Kutch Gau Seva Mandal", type: "Gaushala", domain: "animal-seva", city: "Bhuj", submitted: "2026-07-08", stage: "Under review", docs: 4, docsTotal: 6 },
];

export const auditLog = [
  { id: "AU-9011", actor: "Dr. Pritam Singhvi", action: "Approved campaign", target: "Monsoon ration kits for 1,500 families", at: "2026-07-26 18:12" },
  { id: "AU-9010", actor: "Riddhi Singhvi", action: "Released payout PO-2605", target: "Annapurna Seva Mandal", at: "2026-07-26 11:04" },
  { id: "AU-9009", actor: "System", action: "Flagged stalled campaign", target: "40 tailoring units for women's SHGs", at: "2026-07-25 02:00" },
  { id: "AU-9008", actor: "Sandeep Singhvi", action: "Verified 80G document", target: "Prakriti Rakshak Samiti", at: "2026-07-24 16:45" },
  { id: "AU-9007", actor: "Coolsjes Singhvi", action: "Suspended organisation", target: "Nari Udaan Sangh (docs expired)", at: "2026-07-22 09:30" },
];

export const platformFee = {
  standard: 3,
  emergency: 0,
  note: "A flat 3% platform fee covers payment gateway, receipt automation, audit and support. Emergency medical campaigns are carried at 0%.",
};

export const aggregatorStats = [
  { label: "Partner organisations", value: String(partners.length) },
  { label: "Live campaigns", value: String(campaigns.filter((c) => c.status === "Live").length) },
  { label: "Giving domains", value: String(initiatives.length) },
  { label: "Funds routed to partners", value: "Rs 6.8 Cr+" },
];
